import { type EventPrivacy, type ProgramEvent, getProgramEventLastChangeRequestStatus } from 'models';
import { canManageCommunityProfile } from 'permissions';
import { Form, type FormInstance } from 'ui';
import { useUser } from 'features/auth';
import type { EmailSelectedUserWithRole, RegisteredSelectedUserWithRole } from '../../../../types';
import type { CommunityOption } from '../EventCommunity';
import EventInviteesAlert from './EventInviteesAlert';

export type ExceedCommonFormValues = {
  community?: CommunityOption;
  emailInvitees?: EmailSelectedUserWithRole[];
  invitees?: RegisteredSelectedUserWithRole[];
  viewPermission: EventPrivacy;
};

type P = {
  event?: ProgramEvent;
  form: FormInstance<ExceedCommonFormValues>;
};

const EventInviteesExceededLimit = ({ event, form }: P) => {
  const { railsUserData: viewer } = useUser();

  return (
    <Form.Item<ExceedCommonFormValues>
      noStyle
      shouldUpdate={(previous, current) =>
        previous.community !== current.community ||
        previous.invitees !== current.invitees ||
        previous.emailInvitees !== current.emailInvitees ||
        previous.viewPermission !== current.viewPermission
      }
    >
      {() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        const formValues: Partial<ExceedCommonFormValues> = form.getFieldsValue(true);

        const totalPrivateInvitees = (formValues.invitees ?? []).length + (formValues.emailInvitees ?? []).length;

        const totalPublicInvitees =
          (formValues.community?.data.membersCount ?? 0) + (formValues.emailInvitees ?? []).length;

        const totalInvitees = formValues.viewPermission === 'private' ? totalPrivateInvitees : totalPublicInvitees;

        const inviteesCount = event?.inviteesCount ?? 0;
        const isLimitAbove250Exceeded = inviteesCount > 25 && inviteesCount <= 250 && totalInvitees > 250;
        const isLimitAbove25Exceeded = inviteesCount <= 25 && totalInvitees > 25;

        const isEventApproved = (event && getProgramEventLastChangeRequestStatus(event)) === 'approved';
        const isApprovedInviteesLimitExceeded = isEventApproved && (isLimitAbove250Exceeded || isLimitAbove25Exceeded);

        const exceededApprovedInviteesLimit = isApprovedInviteesLimitExceeded
          ? // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
            isLimitAbove25Exceeded
            ? 25
            : 250
          : undefined;

        const isAdmin = Boolean(
          formValues.community && canManageCommunityProfile({ community: formValues.community.data, viewer }),
        );

        return (
          <EventInviteesAlert
            exceededApprovedInviteesLimit={exceededApprovedInviteesLimit}
            isAdmin={isAdmin}
            isEventApproved={isEventApproved}
            totalInvitees={totalInvitees}
          />
        );
      }}
    </Form.Item>
  );
};

export default EventInviteesExceededLimit;
